import React, { useState } from "react";
import { LightbulbIcon } from "lucide-react";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";
import { useAppDispatch } from "src/hooks/redux";
import jsCookie from "js-cookie";
import { useSearchParams } from "react-router-dom";
import { updateObjectives } from "../action";

interface Objective {
    id: number;
    name: string;
    percentage: number;
}

interface ObjectiveConfirmationProps {
    json_response: Objective[] | null;
    onContinue: () => void
}

const ObjectiveConfirmation: React.FC<ObjectiveConfirmationProps> = ({
    json_response,
    onContinue
}) => {

    const dispatch = useAppDispatch();
    const userId = jsCookie.get("user_id");

    const [searchParams] = useSearchParams();
    const threadId = searchParams.get("threadId");

    const [objectives, setObjectives] = useState(json_response || []);
    const [open, setOpen] = useState<boolean>(true);

    const handleSliderChange = (id: number, newPercentage: number) => {
        setObjectives((prevObjectives) =>
            prevObjectives.map((objective) =>
                objective.id === id
                    ? { ...objective, percentage: newPercentage }
                    : objective
            )
        );
    };

    const [loading, setLoading] = useState<boolean>(false);

    const handleContinue = async () => {
        setLoading(true)
        // Logic to handle the continue action, e.g., save changes or proceed to the next step
       
        // Here you might want to send the updated objectives to a backend or context
        if (JSON.stringify(objectives) === JSON.stringify(json_response)) {
             onContinue()
            return;
        }
        else {
            const { data } = await dispatch(
                updateObjectives({
                    objectives: objectives,
                    userId: userId || "",
                    threadId: threadId || "",
                }),
            ).unwrap();
            if (data) {
                onContinue()
                setLoading(false)
            }
         
        }
    };


    return (
        <div
            className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <img
                            src={BookIcon}
                            alt="Book Icon"
                            className="w-3 h-3 object-contain"
                        />
                        <h2 className="text-lg font-semibold">
                            Objective Confirmation & Weight Calibration
                        </h2>
                    </div>
                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                        aria-label="Toggle Panel"
                    >
                        <img
                            src={SwitchIcon}
                            alt="Toggle"
                            className="w-3 h-3 object-contain"
                        />
                    </button>
                </div>

                {/* Main Content */}
                {open && (
                    <div className="h-[55vh] overflow-y-auto pn_scroller flex flex-col">
                        <h3 className="text-base font-semibold  mb-1">
                            Weight Sliders
                        </h3>

                        <div className="border border-[#D9D9D9] rounded-lg">
                            <div className="grid grid-cols-3 items-center text-base font-semibold sticky top-0 bg-gray-100 z-10 py-2 px-1">
                                <div className="col-span-2">Objective</div>
                                <div className="text-right pr-2">Weight</div>
                            </div>

                            {objectives.map((objective) => (
                                <div
                                    key={objective.id}
                                    className="last:border-0 border-b border-[#D9D9D9] grid grid-cols-3 items-center  hover:bg-gray-50 py-2 px-1"
                                >
                                    <div className="col-span-1 text-xs text-gray-700">
                                        {objective.name}
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-900 w-8 text-right">
                                            {objective.percentage}%
                                        </span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={objective.percentage}
                                            onChange={(e) =>
                                                handleSliderChange(objective.id, Number(e.target.value))
                                            }
                                            style={{
                                                background:
                                                    `linear-gradient(to right, #442873 ${objective.percentage}%, #e5e7eb ${objective.percentage}%)`,
                                            }}
                                            className="
                                                w-full h-2 rounded-lg appearance-none
                                                [&::-webkit-slider-thumb]:appearance-none
                                                [&::-webkit-slider-thumb]:h-3
                                                [&::-webkit-slider-thumb]:w-3
                                                [&::-webkit-slider-thumb]:rounded-full
                                                [&::-webkit-slider-thumb]:bg-[#442873]
                                                [&::-webkit-slider-thumb]:cursor-pointer
                                                [&::-moz-range-thumb]:h-3
                                                [&::-moz-range-thumb]:w-3
                                                [&::-moz-range-thumb]:rounded-full
                                                [&::-moz-range-thumb]:bg-[#442873]
                                            "
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bottom Note */}
                {open && (
                    <div className="flex items-start gap-2 text-xs text-gray-600 mt-4 px-1">
                        <LightbulbIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span>
                            Do these objectives and weights reflect your priorities? Adjust if
                            needed — this will shape how evidence is scored and reported.
                        </span>
                    </div>
                )}

                {/* Footer Actions */}
                {open && (
                    <div className="mt-4 pt-3 border-t border-[#DBDEEB] flex flex-col items-end">
                        <button
                            onClick={handleContinue}
                            disabled={loading}
                            className="cursor-pointer rounded-lg px-2 py-1 border bg-secondary-500 text-[#373D3F]"
                        >
                            {loading ? "Updating" : "Accept & Continue"}
                        </button>
                    </div>
                )}
            </div>
        </div >
    );
};

export default ObjectiveConfirmation;
